import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
    HttpResponse,
    HttpBackend,
    HttpClient
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from './services/token.service';
import { AuthService } from './services/auth.service';
import { ApiUrls } from './api-url';
import { AppConfigService } from './services/app-config.service';

interface GetAccessResponse {
    originalResponse: {
        StatusCode: number | null;
        Error: string | null;
        Message: string | null;
        Data: {
            ID: number;
            EmployeeID: number;
            EmpNo: number;
            MarkaziaUserID: string | null;

            FirstName: string | null;
            SecondName: string | null;
            ThirdName: string | null;
            LastName: string | null;

            FirstNameAr: string | null;
            SecondNameAr: string | null;
            LastNameAr: string | null;

            Email: string | null;
            MobileNumber: string | null;
            JobTitle: string | null;
            Name: string | null;

            IsActivite: boolean | null;
            Status: string | null;

            GenderID: number | null;
            GroupID: number | null;
            BusinessAreaID: number | null;
            WorkplaceClassificationID: number | null;
            ContractTypeID: number | null;

            CreatedAt: string | null;
            ModifiedAt: string | null;
            CreatedBy: number | null;
            ModifiedBy: number | null;
            IsDeleted: boolean | null;

            UserWorkingHours: any[];
            UserBusinessAreas: any[];
            Group: any | null;
            UserRoles: any[];
        } | null;
        Status: boolean | null;
    };
    enhancedUserDetails?: {
        userId: number;
        employeeId: number;
        identifierId: number;
        mobile?: string | null;
        city?: string | null;
        roles?: {
            roleId: number;
            roleName: string;
        };
        userDetails?: {
            fullName: string;
            userEmail: string | null;
            userJobTitle: string | null;
            profileImage: string | null;
            userType?: string | null;
            mainBusinessArea?: {
                businessAreaID: number;
                baName: string;
            };
        };
    };
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private getAccessEndpoint = `${this.appConfig.masterBaseUrl.replace(/\/+$/, '')}/${ApiUrls.User.GetAccess.replace(/^\/+/, '')}`;
    private refreshEndpoint = `${this.appConfig.masterBaseUrl.replace(/\/+$/, '')}/${ApiUrls.User.RefreshThirdPartyToken.replace(/^\/+/, '')}`;
    private rawHttp: HttpClient;

    constructor(
        private tokenStore: TokenService,
        private authService: AuthService,
        private router: Router,
        handler: HttpBackend,
        private appConfig: AppConfigService
    ) {
        this.rawHttp = new HttpClient(handler);
    }

    private isTokenEndpoint(url: string): boolean {
        return url.startsWith(this.getAccessEndpoint) || url.startsWith(this.refreshEndpoint);
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const localToken = this.tokenStore.token;
        const ssoAccess = this.tokenStore.ssoAccessToken;
        const inSsoMode = this.tokenStore.isSingleSignOnMode;

        const tokenToUse = this.isTokenEndpoint(req.url)
            ? (ssoAccess || localToken || null)
            : (localToken || ssoAccess || null);

        const authedReq = tokenToUse ? this.addAuth(req, tokenToUse) : req;

        return next.handle(authedReq).pipe(
            mergeMap(event => {
                if (event instanceof HttpResponse) {
                    const body = event.body;

                    if (
                        event.url?.startsWith(this.getAccessEndpoint) &&
                        body?.originalResponse?.Status === true
                    ) {
                        this.saveUserFromGetAccess(body as GetAccessResponse);
                    }

                    if (
                        body?.Status === false &&
                        (body?.Error === 'Token has expired' || body?.Message === 'Token has expired')
                    ) {
                        const ssoRefresh = this.tokenStore.ssoRefreshToken;
                        if (inSsoMode && ssoRefresh) {
                            return this.refreshAndRetry(req, next, ssoRefresh);
                        }
                    }
                }
                return of(event);
            }),
            catchError((err: any) => this.handleError(err, authedReq, next))
        );
    }

    private saveUserFromGetAccess(res: GetAccessResponse) {
        const data = res?.originalResponse?.Data;
        const enhanced = res?.enhancedUserDetails;

        if (!data) return;

        const id =
            data.EmployeeID && data.EmployeeID !== 0
                ? data.EmployeeID
                : data.ID && data.ID !== 0
                    ? data.ID
                    : data.MarkaziaUserID ?? null;

        const fullName =
            enhanced?.userDetails?.fullName ||
            [data.FirstName, data.SecondName, data.ThirdName, data.LastName]
                .filter(Boolean)
                .join(' ') ||
            data.Email ||
            '';

        const email = enhanced?.userDetails?.userEmail || data.Email || '';

        this.tokenStore.saveUser({
            nameid: id,
            UserID: id,
            EmployeeID: data.EmployeeID ?? data.ID ?? null,
            EmpNo: data.EmpNo ?? null,
            Email: email,
            fullName,
            userPortals: [],
            role: enhanced?.roles?.roleName ?? null,
            city: enhanced?.city ?? null,
            profileImage: enhanced?.userDetails?.profileImage ?? ''
        });

        this.tokenStore.saveEmployeeInfo(res);
    }

    private handleError(err: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (
            err instanceof HttpErrorResponse &&
            (err.status === 401 ||
                (err.status === 400 && /unauthorized/i.test(err.error?.errors?.[0]?.errorMessageEn ?? '')))
        ) {
            const message = err?.error?.Message || err?.error?.Error || err?.message || '';
            const expired = /expired|invalid/i.test(message);
            if (expired) {
                const refreshToken = this.tokenStore.ssoRefreshToken;
                if (refreshToken) {
                    return this.refreshAndRetry(req, next, refreshToken);
                }
            }
        }
        return throwError(() => err);
    }

    private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler, refreshToken: string): Observable<HttpEvent<any>> {
        const { isRefreshing, tokenSubject } = this.authService.getRefreshState();

        if (!isRefreshing) {
            return this.authService.refreshToken(refreshToken).pipe(
                switchMap(({ accessToken }) => {
                    const retried = this.addAuth(req, accessToken);
                    return next.handle(retried);
                }),
                catchError(e => throwError(() => e))
            );
        }

        return tokenSubject.pipe(
            filter(t => !!t),
            take(1),
            switchMap(t => next.handle(this.addAuth(req, t!)))
        );
    }

    /**
     * Dynamically attaches the correct header for local or SSO authentication.
     */
    private addAuth(req: HttpRequest<any>, token: string): HttpRequest<any> {
        const url = req.url.toLowerCase();

        // Skip adding auth header for specific endpoints
        if (url.includes('getaccess') || url.includes('refreshthirdpartytoken')) {
            return req;
        }

        const headerName = this.tokenStore.isSingleSignOnMode
            ? 'HubAuthorization'
            : 'Authorization';

        return req.clone({
            setHeaders: { [headerName]: `Bearer ${token}` }
        });
    }
}
